export interface NavSection {
  title: string;
  items: { id: string; label: string }[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    title: "Getting started",
    items: [
      { id: "overview", label: "Overview" },
      { id: "install", label: "Installation" },
      { id: "quickstart", label: "Quick start" },
      { id: "claude-code", label: "Using with agents" },
      { id: "data-sources", label: "Data sources" },
    ],
  },
  {
    title: "Search & discovery",
    items: [
      { id: "search", label: "search" },
      { id: "searches", label: "searches" },
      { id: "lookup", label: "lookup" },
      { id: "grep", label: "grep" },
      { id: "scan", label: "scan" },
      { id: "sql", label: "sql" },
      { id: "export", label: "export" },
    ],
  },
  {
    title: "Analysis",
    items: [
      { id: "map", label: "map" },
      { id: "reduce", label: "reduce" },
      { id: "filter", label: "filter" },
      { id: "ask-image", label: "ask-image" },
      { id: "routines", label: "routines" },
    ],
  },
  {
    title: "File operations",
    items: [
      { id: "cat", label: "cat" },
      { id: "head-tail", label: "head / tail" },
      { id: "ls", label: "ls" },
      { id: "tree", label: "tree" },
      { id: "wc", label: "wc" },
      { id: "cd", label: "cd / pwd" },
    ],
  },
  {
    title: "Text processing",
    items: [
      { id: "sort", label: "sort / uniq / cut / tr" },
      { id: "sed", label: "sed" },
      { id: "awk", label: "awk" },
      { id: "jq", label: "jq" },
      { id: "bash-pipes", label: "Pipes & bash" },
    ],
  },
  {
    title: "Results & output",
    items: [
      { id: "results", label: "results" },
      { id: "redirection", label: "Redirection" },
    ],
  },
  {
    title: "Paper repos",
    items: [
      { id: "repos-overview", label: "Overview" },
      { id: "repo", label: "git" },
      { id: "init", label: "git init" },
      { id: "checkout", label: "git checkout" },
      { id: "add", label: "git add" },
      { id: "remove", label: "git remove" },
      { id: "commit", label: "git commit" },
      { id: "status", label: "git status" },
      { id: "log", label: "git log" },
      { id: "history", label: "git history" },
      { id: "branch", label: "git branch" },
      { id: "merge", label: "git merge" },
      { id: "repo-export", label: "git export" },
      { id: "citations", label: "git citations" },
    ],
  },
  {
    title: "Configuration",
    items: [
      { id: "auth", label: "Authentication" },
      { id: "api-keys", label: "API keys" },
      { id: "http-api", label: "HTTP API" },
      { id: "config", label: "Config" },
      { id: "rate-limits", label: "Rate limits" },
      { id: "skill-install", label: "Skill install" },
      { id: "update", label: "Update" },
      { id: "filesystem", label: "Filesystem layout" },
    ],
  },
  {
    title: "Clipboard",
    items: [
      { id: "clipboard-overview", label: "Overview" },
      { id: "clipboard-upload", label: "Adding documents" },
      { id: "clipboard-chrome-extension", label: "Chrome extension" },
      { id: "clipboard-browse", label: "Browsing & organizing" },
      { id: "clipboard-search", label: "Searching" },
      { id: "clipboard-share", label: "Sharing" },
      { id: "clipboard-sync", label: "Syncing" },
      { id: "clipboard-manage", label: "Deleting files" },
      { id: "clipboard-limits", label: "Limits" },
    ],
  },
  {
    title: "Python SDK",
    items: [
      { id: "sdk-overview", label: "Overview" },
      { id: "sdk-install", label: "Install & auth" },
      { id: "sdk-quickstart", label: "Quick start" },
      { id: "sdk-search", label: "client.search" },
      { id: "sdk-lookup", label: "client.lookup" },
      { id: "sdk-sql", label: "client.sql" },
      { id: "sdk-map", label: "client.map_" },
      { id: "sdk-pull", label: "client.pull" },
      { id: "sdk-ask-image", label: "client.ask_image" },
      { id: "sdk-bash", label: "client.bash" },
      { id: "sdk-results", label: "client.results" },
      { id: "sdk-papers", label: "client.papers.*" },
      { id: "sdk-execute", label: "execute / stream" },
      { id: "sdk-errors", label: "Errors & types" },
    ],
  },
];

interface DocsSidebarProps {
  activeSection: string;
  onNavigate: (id: string) => void;
}

export const DocsSidebar = ({ activeSection, onNavigate }: DocsSidebarProps) => {
  const sections = NAV_SECTIONS;
  return (
    <nav aria-label="Documentation" className="py-6 px-4 lg:py-8 lg:pr-6 lg:pl-0">
      {sections.map((section) => (
        <div key={section.title} className="mb-6">
          <h4 className="text-2xs font-semibold uppercase tracking-[0.08em] text-zinc-500 mb-2 px-2.5">
            {section.title}
          </h4>
          <ul className="space-y-0.5">
            {section.items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => onNavigate(item.id)}
                  aria-current={activeSection === item.id ? "true" : undefined}
                  className={[
                    "w-full text-left px-2.5 py-1 rounded-[3px] text-sm transition-all border-l-2",
                    activeSection === item.id
                      ? "text-zinc-900 font-medium border-l-zinc-900"
                      : "text-zinc-400 hover:text-zinc-700 hover:bg-zinc-50 border-l-transparent",
                  ].join(" ")}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
};
